import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Star, Users, Clock, Timer, ArrowLeft, ChefHat, Gauge, CheckCircle2, MapPin, Lightbulb, EyeOff } from "lucide-react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SpiceBadge, CuisineBadge, DietBadge } from "../components/badges";
import { RatingStars, RatingCount } from "../components/RatingStars";
import { SaveButton } from "../components/SaveButton";
import { RecipeVideo } from "../components/RecipeVideo";
import { CookMode } from "../components/CookMode";
import { LoadingState, RecipeEmptyState } from "../components/states";
import { AdSlot } from "../components/AdSlot";
import { AD_SLOTS } from "../lib/adsense";
import { RichText, stripMarkdown } from "../lib/richtext";
import { CommentThread } from "../components/CommentThread";
import { ReviewList, RateBox } from "../components/ReviewList";
import { ShareButtons } from "../components/ShareButtons";
import { toast } from "sonner";

function Schema({ r }) {
  useEffect(() => {
    if (!r) return;
    const data = {
      "@context": "https://schema.org/",
      "@type": "Recipe",
      name: r.title,
      description: r.description,
      image: [r.thumbnail],
      author: { "@type": "Person", name: r.creator?.display_name },
      recipeCuisine: r.cuisine,
      totalTime: `PT${(r.prep_time || 0) + (r.cook_time || 0)}M`,
      recipeIngredient: (r.ingredients || []).map((i) => `${i.quantity || ""} ${i.unit || ""} ${i.name}`.trim()),
      recipeInstructions: (r.instructions || []).map((s) => ({ "@type": "HowToStep", text: stripMarkdown(s) })),
      aggregateRating: { "@type": "AggregateRating", ratingValue: r.rating_avg, ratingCount: r.rating_count },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, [r]);
  return null;
}

function MadeThis({ recipeId, made, count, onChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!user) { toast("Log in to mark recipes you've cooked"); navigate("/login"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/recipes/${recipeId}/made`); onChange(data); toast.success(data.made ? "Added to your cooked list — nice work!" : "Removed"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid="made-this-button" aria-pressed={made}
      className={`inline-flex items-center gap-2 h-12 px-5 rounded-full font-bold transition-colors disabled:opacity-60 ${made ? "bg-leaf text-white" : "bg-white border border-border text-ink hover:border-leaf hover:text-leaf"}`}>
      <CheckCircle2 size={18} /> {made ? "You made this" : "I made this"}
      <span className={`text-xs font-black rounded-full px-2 py-0.5 ${made ? "bg-white/20" : "bg-secondary text-ink-soft"}`} data-testid="made-count">{count}</span>
    </button>
  );
}

export default function RecipeDetail() {
  const { slug, recipeSlug, chefSlug } = useParams();
  const theSlug = recipeSlug || slug;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cookOpen, setCookOpen] = useState(false);
  const [community, setCommunity] = useState({ interactions: [], reviews: [], made_count: 0, i_made: false });

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    api.get(`/recipes/${theSlug}`)
      .then(({ data }) => {
        setR(data);
        if (data.creator?.slug && chefSlug && data.creator.slug !== chefSlug) navigate(`/chefs/${data.creator.slug}/${data.slug}`, { replace: true });
        api.get(`/recipes/${data.id}/community`).then((c) => setCommunity(c.data)).catch(() => {});
        const hash = window.location.hash;
        if (hash === "#video" || hash === "#community") setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 300);
      })
      .catch(() => setR(false))
      .finally(() => setLoading(false));
  }, [theSlug, chefSlug, navigate, user]);

  if (loading) return <LoadingState label="Loading recipe..." />;
  if (!r) return <div className="py-16"><RecipeEmptyState title="Recipe not found" subtitle="It may have been removed or unpublished." /></div>;

  const chef = r.creator;
  const chefPath = chef ? `/chefs/${chef.slug}` : null;
  const recipePath = chef ? `/chefs/${chef.slug}/${r.slug}` : `/recipe/${r.slug}`;
  const setInteractions = (fn) => setCommunity((c) => ({ ...c, interactions: typeof fn === "function" ? fn(c.interactions) : fn }));

  return (
    <div className="pb-10" data-testid="recipe-detail">
      <Schema r={r} />

      {/* Hero image */}
      <div className="relative h-[52vh] max-h-[560px] min-h-[320px] w-full overflow-hidden">
        <img src={r.thumbnail} alt={r.title} className="w-full h-full object-cover" data-testid="recipe-hero-photo" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
        <div className="absolute top-4 left-4">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-4 h-10 font-bold text-ink shadow-soft hover:scale-105 transition-transform" data-testid="back-button">
            <ArrowLeft size={17} /> Back
          </button>
        </div>
        {r.status !== "PUBLISHED" && (
          <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-gold text-ink px-3 h-9 text-xs font-black uppercase tracking-wider" data-testid="recipe-status-badge"><EyeOff size={13} /> {r.status === "PENDING" ? "In review" : r.status}</span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
          <div className="max-w-4xl mx-auto">
            {chef && (
              <Link to={chefPath} data-testid="recipe-chef-link" className="inline-flex items-center gap-2.5 rounded-full bg-white/95 backdrop-blur pl-1 pr-4 h-11 shadow-soft hover:bg-white transition-colors mb-3 group">
                <img src={chef.avatar} alt={chef.display_name} className="w-9 h-9 rounded-full object-cover" />
                <span className="text-sm font-black text-ink group-hover:text-coral transition-colors inline-flex items-center gap-1.5"><ChefHat size={14} className="text-coral" /> by {chef.display_name}</span>
                {chef.city && <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-ink-soft"><MapPin size={12} /> {chef.city}</span>}
              </Link>
            )}
            <div className="flex flex-wrap gap-2 mb-3">
              <SpiceBadge level={r.spice_level} />
              <CuisineBadge cuisine={r.cuisine} region={r.region} />
              <DietBadge diet={r.diet} />
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight drop-shadow" data-testid="recipe-title">{r.title}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-6 relative">
        {/* Meta card */}
        <div className="rounded-3xl bg-white border border-border shadow-lift p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-2" data-testid="recipe-rating">
              <RatingStars value={r.rating_avg} size={16} />
              <span className="text-lg font-black text-ink">{r.rating_avg}</span>
              <RatingCount count={r.rating_count} />
            </span>
            <div className="h-8 w-px bg-border hidden sm:block" />
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-soft"><Gauge size={15} className="text-coral" /> {r.difficulty === "Advanced" ? "Hard" : r.difficulty}</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-soft"><Clock size={15} className="text-coral" /> {(r.prep_time || 0) + (r.cook_time || 0)} min</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-soft"><Users size={15} className="text-coral" /> Serves {r.servings}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCookOpen(true)} data-testid="cook-mode-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink text-white hover:bg-ink-soft px-5 h-12 font-bold transition-colors">
              <ChefHat size={18} /> Cook Mode
            </button>
            <SaveButton recipeId={r.id} initialSaved={r.is_saved} variant="full" />
          </div>
        </div>

        <CookMode
          open={cookOpen}
          onClose={() => setCookOpen(false)}
          title={r.title}
          ingredients={(r.ingredients || []).map((i) => `${[i.quantity, i.unit].filter(Boolean).join(" ")} ${i.name}`.trim())}
          method={(r.instructions || []).map(stripMarkdown)}
        />

        {r.description && <p className="mt-6 text-lg text-ink-soft leading-relaxed">{r.description}</p>}

        {r.background && (
          <div className="mt-8 rounded-3xl bg-white border border-border p-6 sm:p-8" data-testid="recipe-background">
            <p className="text-xs font-bold uppercase tracking-widest text-coral">History & background</p>
            <h2 className="font-heading text-2xl font-black text-ink mt-1 mb-3">The story</h2>
            <RichText text={r.background} className="text-ink-soft text-base sm:text-lg" />
          </div>
        )}

        {/* Quick metadata */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Timer, label: "Prep", value: `${r.prep_time} min` },
            { icon: Clock, label: "Cook", value: `${r.cook_time} min` },
            { icon: Users, label: "Servings", value: r.servings },
            { icon: Star, label: "Difficulty", value: r.difficulty === "Advanced" ? "Hard" : r.difficulty },
          ].map((m) => (
            <div key={m.label} className="rounded-2xl bg-white border border-border p-4 text-center">
              <m.icon size={18} className="mx-auto text-coral mb-1.5" />
              <p className="text-xs font-bold uppercase tracking-wide text-ink-soft">{m.label}</p>
              <p className="font-heading font-black text-ink">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Video */}
        {r.youtube_id && (
          <div id="video" className="mt-10 scroll-mt-20">
            <h2 className="font-heading text-2xl font-black text-ink mb-4">Watch it made</h2>
            <RecipeVideo youtubeId={r.youtube_id} title={r.title} />
          </div>
        )}

        {/* Ingredients + Instructions */}
        <div className="mt-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-8">
          <div>
            <h2 className="font-heading text-2xl font-black text-ink mb-4">Ingredients</h2>
            <ul className="rounded-3xl bg-white border border-border shadow-soft divide-y divide-border overflow-hidden" data-testid="ingredient-list">
              {(r.ingredients || []).map((ing, i) => (
                <li key={i} className="flex items-center gap-3 px-5 py-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-coral shrink-0" />
                  <span className="font-semibold text-ink">
                    {[ing.quantity, ing.unit].filter(Boolean).length > 0 && <span className="text-coral font-black mr-1.5">{[ing.quantity, ing.unit].filter(Boolean).join(" ")}</span>}{ing.name}
                  </span>
                </li>
              ))}
            </ul>
            <AdSlot slot={AD_SLOTS.recipe} name="recipe" className="mt-6" />
          </div>

          <div>
            <h2 className="font-heading text-2xl font-black text-ink mb-4">Method</h2>
            <ol className="flex flex-col gap-4" data-testid="instruction-steps">
              {(r.instructions || []).map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="shrink-0 grid place-items-center w-10 h-10 rounded-2xl bg-ink text-white font-heading font-black">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="pt-1.5 text-ink leading-relaxed min-w-0"><RichText text={step} /></div>
                </li>
              ))}
            </ol>
            {r.tips && (
              <div className="mt-8 rounded-3xl bg-gold/10 border border-gold/30 p-6" data-testid="chef-tips">
                <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700"><Lightbulb size={14} /> Chef tips</p>
                <RichText text={r.tips} className="mt-2 text-ink" />
              </div>
            )}
          </div>
        </div>

        {/* Chef card */}
        {chef && (
          <Link to={chefPath} data-testid="recipe-chef-card" className="mt-12 flex items-center gap-4 rounded-3xl bg-white border border-border shadow-soft p-5 hover:shadow-lift hover:-translate-y-0.5 transition-all group">
            <img src={chef.avatar} alt={chef.display_name} className="w-16 h-16 rounded-2xl object-cover border border-border" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-widest text-coral">Recipe by</p>
              <p className="font-heading text-xl font-black text-ink group-hover:text-coral transition-colors truncate">{chef.display_name}</p>
              <p className="text-sm text-ink-soft font-semibold truncate">{[chef.city, `${chef.recipe_count} recipes`, chef.rating_avg ? `★ ${chef.rating_avg}` : null].filter(Boolean).join(" · ")}</p>
            </div>
            <span className="hidden sm:inline-flex h-11 px-5 items-center rounded-full bg-ink text-white font-bold text-sm">View chef</span>
          </Link>
        )}

        {/* Community */}
        <section id="community" className="mt-14 scroll-mt-24" data-testid="recipe-community">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black text-ink">Community</h2>
              <p className="text-ink-soft mt-1">Cooked it? Tell everyone how it went.</p>
            </div>
            <MadeThis recipeId={r.id} made={community.i_made} count={community.made_count} onChange={(d) => setCommunity((c) => ({ ...c, i_made: d.made, made_count: d.made_count }))} />
          </div>

          <div className="mt-8 grid lg:grid-cols-[1fr_1fr] gap-6 items-start">
            <RateBox testId="rate-section" title="How was this recipe?" subtitle="Tap a star, add a few words if you like."
              postUrl={`/recipes/${r.id}/rate`} initialValue={r.my_rating || 0} initialReview={r.my_review}
              onRated={(d) => { setR((p) => ({ ...p, rating_avg: d.rating_avg, rating_count: d.rating_count, my_rating: d.my_rating })); api.get(`/recipes/${r.id}/community`).then((c) => setCommunity(c.data)).catch(() => {}); }} />
            <div>
              <h3 className="font-heading text-xl font-black text-ink mb-3">Recent reviews</h3>
              <ReviewList reviews={community.reviews} />
            </div>
          </div>

          <div className="mt-10">
            <CommentThread items={community.interactions} setItems={setInteractions} postUrl={`/recipes/${r.id}/interactions`} testId="recipe-thread" />
          </div>

          <div className="mt-10">
            <p className="text-sm font-bold text-ink-soft mb-2">Share this recipe</p>
            <ShareButtons title={r.title} path={recipePath} />
          </div>
        </section>

        <div className="mt-12 text-center">
          <Link to="/chefs" className="font-bold text-coral hover:underline">Discover more chefs →</Link>
        </div>
      </div>
    </div>
  );
}
